import { Injectable } from "@angular/core";
import { CommonMpSdk, Mattertag, MpSdk } from "@matterport/webcomponent";
import { map, switchMap, takeUntil, withLatestFrom } from "rxjs/operators";
import { firstValueFrom, Subject, Subscription } from "rxjs";
import { ChartDataService } from "../../../services/chart-data.service";
import {
  DateRange,
  TimeSliderService,
} from "../../../services/time-slider.service";
import { WidgetService } from "../../../services/widget.service";
import { Store } from "@ngrx/store";
import { AppState } from "../../../store/reducers";
import {
  AssetViewModel,
  ChartViewModel,
  HelixChartDataClient,
  WidgetSettings,
} from "@helix/client";
import {
  createWidgetSettings,
  getAllTagIdsForWidget,
} from "../../../pages/tabs/widget-editor/widget-editor.util";
import { MattertagAssetLink } from "../matterport-page/mattertag-asset-link.model";
import { hasProperty, traverseAssetTree } from "../../../helpers";
import { selectTags } from "../../../store/selectors/admin.selectors";
import { WidgetHighchartsAdapterService } from "../widget/highcharts/widget-highcharts-adapter.service";
import { WidgetHighchartsAdapterFactory } from "../widget/highcharts/widget-highcharts-adapter.factory";
import { Options } from "highcharts";

@Injectable({ providedIn: "root" })
export class MattertagInjectorService {
  constructor(
    private readonly widgetHighchartsAdapterService: WidgetHighchartsAdapterService,
    private readonly chartDataService: ChartDataService,
    private readonly timeSliderService: TimeSliderService,
    private readonly widgetService: WidgetService,
    private readonly widgetHighchartsAdapterFactory: WidgetHighchartsAdapterFactory,
    private readonly store: Store<AppState>,
    private readonly helixChartDataClient: HelixChartDataClient,
  ) {}

  private readonly subscriptions: Subscription[] = [];
  private readonly injectedTags = new Set<string>();
  private readonly mattertagHtmlSize: CommonMpSdk.Size = { w: 600, h: 300 };
  private sandboxId: string;
  private messenger: Mattertag.IMessenger;
  private currentTag: string | null = null;

  private containerSize = { width: 750, height: 400 }; // TODO: Set if needed

  public async registerSandbox(mpSdk: MpSdk): Promise<string> {
    console.debug("[MattertagInjectorService] Registering sandbox...");
    // language=HTML
    const html = `
      <html lang="en">
      <head>
        <title>HELIX&trade; Mattertag Injector Service</title>
        <style>
          #container {
            height: calc(100vh - 3rem);
            padding: 0.5rem;
            background-color: white;
            display: none;
          }

          #error {
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 16pt;
            font-family: sans-serif;
            padding: 1rem;
            height: calc(100vh - 3rem);
            user-select: none;
          }
        </style>
        <script src="https://cdnjs.cloudflare.com/ajax/libs/moment.js/2.29.4/moment.min.js"></script>
        <script
          src="https://cdnjs.cloudflare.com/ajax/libs/moment-timezone/0.5.40/moment-timezone-with-data.min.js"></script>
        <script src="https://code.highcharts.com/stock/highstock.js"></script>
        <script src="https://code.highcharts.com/stock/highcharts-more.js"></script>
        <script src="https://code.highcharts.com/modules/no-data-to-display.js"></script>
        <script src="https://code.highcharts.com/modules/wordcloud.js"></script>
        <script>
          (() => {
            const container = document.getElementById("container");
            const errorContainer = document.querySelector("#error");

            errorContainer.innerText = "Waiting for Helix...";

            window.on("helix.loadChartOptions", (chartOptions) => {
              errorContainer.style.display = "none";
              container.style.display = "block";
              Highcharts.chart("container", JSON.parse(chartOptions));
            });

            window.on("helix.showErrorMessage", (message) => {
              container.style.display = "none";
              errorContainer.style.display = "flex";
              errorContainer.innerText = message;
            });

            window.send("helix.ready");
          })();
        </script>
      </head>

      <body>
      <div id="error"></div>
      <div id="container"></div>
      </body>
      </html>`;
    [this.sandboxId, this.messenger] = await mpSdk.Tag.registerSandbox(html, {
      size: this.mattertagHtmlSize,
      name: "HELIX Mattertag Injector Service",
    });
    console.debug(
      "[MattertagInjectorService] Registered sandbox",
      this.sandboxId,
    );
    return this.sandboxId;
  }

  public async linkMattertagToHelix(
    mpSdk: MpSdk,
    destroyed$: Subject<void>,
    link: MattertagAssetLink,
    mattertagId: string,
  ): Promise<void> {
    if (this.currentTag !== mattertagId) {
      this.cancelAnyChartDataSubscriptions();
      this.currentTag = mattertagId;
    }

    if (!this.injectedTags.has(mattertagId)) {
      await mpSdk.Tag.attach(mattertagId, this.sandboxId);
      this.injectedTags.add(mattertagId);
      console.debug(
        "[MattertagInjectorService] Attached sandbox",
        this.sandboxId,
        "to Mattertag",
        mattertagId,
      );
    }

    setTimeout(() => {
      if (link.hasOwnProperty("error")) {
        this.injectErrorMessage(link.error);
        return;
      }

      if (hasProperty(link, "widgetId", "number")) {
        this.injectWidgetByWidgetId(destroyed$, link.widgetId);
        return;
      }

      if (hasProperty(link, "tagIds", "object")) {
        this.injectWidgetByTagIds(destroyed$, link.tagIds);
        return;
      }

      if (hasProperty(link, "assetId", "number")) {
        this.injectWidgetByAssetId(destroyed$, link.assetId);
        return;
      }

      const assets$ = this.store
        .select((s) => s.dashboard.assetTree)
        .pipe(map((assets) => traverseAssetTree(assets)));

      firstValueFrom(assets$).then((assetList) => {
        const asset = this.findAssetFromMattertagAssetLink(assetList, link);
        const assetId = asset?.assetId ?? null;
        this.injectWidgetByAssetId(destroyed$, assetId);
      });
    }, 500);
  }

  private cancelAnyChartDataSubscriptions(): void {
    console.debug(
      "[MattertagInjectorService] Cancelling old chart data subscriptions",
    );
    while (this.subscriptions.length > 0) {
      this.subscriptions.pop().unsubscribe();
    }
  }

  private injectWidgetByWidgetId(destroyed$: Subject<void>, widgetId: number) {
    firstValueFrom(this.widgetService.getWidgetById(widgetId))
      .then((widget) => {
        this.injectWidgetConfiguration(destroyed$, widget);
      })
      .catch(() => {
        const message = `We cannot display a widget for this Mattertag because widget ${widgetId} could not be found.`;
        this.injectErrorMessage(message);
      });
  }

  private injectWidgetConfiguration(
    destroyed$: Subject<void>,
    widget: WidgetSettings,
  ) {
    const timeFunction = WidgetService.deserializeTimeFunction(
      widget.chartStyle.timeFunction,
    );
    const dateRange$ =
      this.widgetService.getDateRangeFromTimeFunction(timeFunction);

    const subscription = this.chartDataService
      .getChartData(
        getAllTagIdsForWidget(widget),
        timeFunction,
        widget.chartStyle.valueFunction,
        this.chartDataService.getChartWidgetTypes(widget),
      )
      .pipe(
        withLatestFrom(dateRange$),
        map(([chartData, dateRange]) =>
          this.widgetHighchartsAdapterFactory
            .getAdapterForWidgetType(
              widget.chartStyle.defaultChartType as any,
              {
                chartData,
                settings: widget,
                xAxisRange: dateRange,
                useDummyData: false,
              },
            )
            .getHighchartsChartOptions(this.containerSize),
        ),
        takeUntil(destroyed$),
      )
      .subscribe({
        next: (chartOptions) => this.injectChartOptions(chartOptions),
        error: (error: unknown) =>
          this.injectErrorMessage(
            (error as Error)?.message ??
              "An unknown error has occurred during widget injection",
          ),
      });

    this.subscriptions.push(subscription);
  }

  private injectErrorMessage(message: string): void {
    this.messenger.send("helix.showErrorMessage", message);
  }

  private injectWidgetByTagIds(destroyed$: Subject<void>, tagIds: number[]) {
    if ((tagIds?.length ?? 0) === 0) {
      this.injectErrorMessage(
        "We cannot display a chart because no tags were specified.",
      );
      return;
    }

    const currentDateRange$ = this.timeSliderService.currentDateRange();
    const chartDataSubscription = currentDateRange$
      .pipe(
        switchMap((dateRange) =>
          this.helixChartDataClient.getChartDataForMultipleTags({
            tags: tagIds,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
          }),
        ),
        withLatestFrom(currentDateRange$),
        takeUntil(destroyed$),
      )
      .subscribe(([chartData, dateRange]) => {
        this.injectChartOptions(
          this.getHighchartsChartOptionsAsJson(chartData, dateRange),
        );
      });

    this.subscriptions.push(chartDataSubscription);
  }

  private injectChartOptions(chartOptions: Options) {
    chartOptions.title = { style: { color: "#fff", fontSize: "1pt" } };
    chartOptions.subtitle = {};
    chartOptions.legend = { enabled: false };
    this.messenger.send("helix.loadChartOptions", JSON.stringify(chartOptions));
  }

  private injectWidgetByAssetId(
    destroyed$: Subject<void>,
    assetId: number | null,
  ) {
    if (assetId === null) {
      const message =
        "We cannot display a chart for this asset because the asset link configuration provided in the " +
        "Mattertag description could not be matched to an asset in the database.";
      this.injectErrorMessage(message);
      return;
    }

    const tagIdsForAsset$ = this.store
      .select(selectTags)
      .pipe(
        map((tags) =>
          tags.filter((tag) => tag.assetId === assetId).map((tag) => tag.tagId),
        ),
      );
    firstValueFrom(tagIdsForAsset$).then((tagIds) => {
      if ((tagIds?.length ?? 0) === 0) {
        this.injectErrorMessage(
          `We cannot display a chart because no tags could be found for asset ${assetId}.`,
        );
        return;
      }
      this.injectWidgetByTagIds(destroyed$, tagIds);
    });
  }

  private getHighchartsChartOptionsAsJson(
    chartData: ChartViewModel | ChartViewModel[],
    xAxisRange: DateRange,
  ): Options {
    const widgetSettings = createWidgetSettings({});
    widgetSettings.xAxisStyle.label = "";
    widgetSettings.yAxisStyles[0].label = "";
    return this.widgetHighchartsAdapterService.getHighchartsChartOptions(
      {
        xAxisRange,
        chartData,
        settings: widgetSettings,
      },
      this.containerSize,
    );
  }

  private findAssetFromMattertagAssetLink(
    assetList: AssetViewModel[],
    mattertagAssetLink: MattertagAssetLink,
  ): AssetViewModel | null {
    return (
      assetList.find(
        (x) =>
          x.assetId === mattertagAssetLink.assetId ||
          (!!x.clientAssetCode &&
            x.clientAssetCode === mattertagAssetLink.clientAssetCode) ||
          x.name === mattertagAssetLink.assetName,
      ) || null
    );
  }
}
